<template>
  <v-card
    class="d-flex flex-column"
    flat
    tile
    :height="!isMobileBreakpoint ? 548 : '100dvh'"
  >
    <div class="header w-full d-flex justify-space-between">
      <v-btn v-if="isIntroStep" icon @click="handleMainBack" class="mr-2">
        <v-icon class="neutral-70--text">mdi-arrow-left</v-icon>
      </v-btn>
      <v-spacer v-else></v-spacer>
      <v-btn icon @click="handleClose">
        <v-icon class="neutral-70--text">mdi-close</v-icon>
      </v-btn>
    </div>

    <v-card-text
      style="overflow-y: auto;"
      class="flex-grow-1 d-flex flex-column align-center"
      :class="[
        isMobileBreakpoint ? 'justify-center' : 'justify-start',
        { 'not-intro-step': !isIntroStep }
      ]"
    >
      <template v-if="isIntroStep">
        <img
          width="80px"
          height="80px"
          :src="currentSlide.image"
          :class="isMobileBreakpoint ? 'mb-3' : 'mb-5'"
        />
      </template>
      <template v-else>
        <base-lazy-image
          class="image-step mb-5"
          :src="currentSlide.image"
          :alt="currentSlide.title"
          :key="currentSlide.image"
        />
        <div class="d-flex justify-center mb-5">
          <v-item-group v-model="indicatorStep" class="d-flex">
            <v-item
              v-for="n in totalSteps - 1"
              :key="`btn-${n}`"
              v-slot="{ active, toggle }"
            >
              <button
                class="indicator-dot"
                :class="{ active: active }"
                @click="toggle"
              ></button>
            </v-item>
          </v-item-group>
        </div>
      </template>

      <h1 
        class="title body-bold-xlarge text-center neutral-20-dark--text"
        :class="{
          'mb-2': !isMobileBreakpoint,
          'mb-7': isMobileBreakpoint,
          'regular-bold-medium': !isIntroStep,
          'not-intro-step': !isIntroStep
        }"
      >
        {{ currentSlide.title }}
      </h1>
      <p
        class="subtitle text-center neutral-40-light--text"
        :class="[
          `subtitle-step-${step}`,
          isIntroStep 
            ? (isMobileBreakpoint ? 'body-regular-base' : 'body-regular-medium')
            : 'regular-small',
          { 'not-intro-step': !isIntroStep }
        ]"
        v-html="currentSlide.subtitle"
      ></p>

      <a
        v-if="currentSlide.linkLabel && currentSlide.linkHref"
        size="small"
        target="_blank"
        rel="noopener"
        type="primary"
        :href="currentSlide.linkHref"
      >
        {{ currentSlide.linkLabel }}
        <v-icon size="14px" class="brand-60-base--text">
          mdi-open-in-new
        </v-icon>
      </a>
    </v-card-text>

    <div
      :class="[
        'card-actions',
        'd-flex',
        'justify-center',
        'align-center'
      ]"
    >
      <template v-if="isIntroStep">
        <base-btn
          @click="handleContinue"
          :type="isMobileBreakpoint ? 'tertiary' : 'neutral'"
          :style="{ width: isMobileBreakpoint ? '50%' : '80px' }"
        >
          {{ $t('message.actions.skip') }}
        </base-btn>
        <base-btn
          @click="handleStepForward"
          :style="{ width: isMobileBreakpoint ? '50%' : '100px' }"
        >
          {{ $t('message.actions.next') }}
        </base-btn>
      </template>
      <template v-else>
        <base-btn
          type="tertiary"
          @click="handleStepBack"
          :style="{ width: isMobileBreakpoint ? '50%' : '80px' }"
        >
          {{ $t('message.actions.return') }}
        </base-btn>
        <base-btn
          @click="handleStepForward"
          :style="{ width: isMobileBreakpoint ? '50%' : '100px' }"
        >
          {{
            isLastStep
              ? $t('message.actions.understand')
              : $t('message.actions.next')
          }}
        </base-btn>
      </template>
    </div>
  </v-card>
</template>

<script>
import i18n from '@/translations/lang'
import { mapState } from 'vuex'
import BaseLazyImage from '@/components/BaseLazyImage.vue'

export default {
  name: 'DubbingBestPracticesTemplate',
  components: {
    BaseLazyImage,
  },
  props: {
    tech: {
      type: String,
      default: 'heygen',
    },
  },
  data() {
    return {
      step: 0,
      slides: {
        elevenlabs: [
          {
            isIntro: true,
            image: require('@/assets/pandaIcons/voice-2.svg'),
            title: i18n.t('message.ai.dubbings.bestPractices.elevenlabs.step1.title'),
            subtitle: i18n.t(
              'message.ai.dubbings.bestPractices.elevenlabs.step1.subtitle'
            ),
          },
          {
            image: require('@/assets/aiWorkflow/Preview-1.png'),
            title: i18n.t('message.ai.dubbings.bestPractices.elevenlabs.step2.title'),
            subtitle: i18n.t(
              'message.ai.dubbings.bestPractices.elevenlabs.step2.subtitle'
            ),
          },
          {
            image: require('@/assets/aiWorkflow/Preview-2.png'),
            title: i18n.t('message.ai.dubbings.bestPractices.elevenlabs.step3.title'),
            subtitle: i18n.t(
              'message.ai.dubbings.bestPractices.elevenlabs.step3.subtitle'
            ),
            linkLabel: i18n.t('message.ai.dubbings.bestPractices.elevenlabs.step3.learnMore'),
            linkHref: i18n.t('message.ai.dubbings.bestPractices.elevenlabs.step3.learnMoreHref'),
          },
        ],
        heygen: [
          {
            isIntro: true,
            image: require('@/assets/pandaIcons/voice-2.svg'),
            title: i18n.t(
              'message.ai.dubbings.bestPractices.heygen.step1.title'
            ),
            subtitle: i18n.t(
              'message.ai.dubbings.bestPractices.heygen.step1.subtitle'
            ),
          },
          {
            image: require('@/assets/aiWorkflow/Preview-3.png'),
            title: i18n.t(
              'message.ai.dubbings.bestPractices.heygen.step2.title'
            ),
            subtitle: i18n.t(
              'message.ai.dubbings.bestPractices.heygen.step2.subtitle'
            ),
          },
          {
            image: require('@/assets/aiWorkflow/Preview-1.png'),
            title: i18n.t(
              'message.ai.dubbings.bestPractices.heygen.step3.title'
            ),
            subtitle: i18n.t(
              'message.ai.dubbings.bestPractices.heygen.step3.subtitle'
            ),
          },
          {
            image: require('@/assets/aiWorkflow/Preview-2.png'),
            title: i18n.t(
              'message.ai.dubbings.bestPractices.heygen.step4.title'
            ),
            subtitle: i18n.t(
              'message.ai.dubbings.bestPractices.heygen.step4.subtitle'
            ),
          },
          {
            image: require('@/assets/aiWorkflow/Preview-4.png'),
            title: i18n.t(
              'message.ai.dubbings.bestPractices.heygen.step5.title'
            ),
            subtitle: i18n.t(
              'message.ai.dubbings.bestPractices.heygen.step5.subtitle'
            ),
            linkLabel: i18n.t('message.ai.dubbings.bestPractices.heygen.step5.learnMore'),
            linkHref: i18n.t('message.ai.dubbings.bestPractices.heygen.step5.learnMoreHref'),
          },
        ],
      },
    }
  },
  computed: {
    ...mapState({
      isMobileBreakpoint: (state) => state.user.isMobileBreakpoint,
    }),
    currentLanguage() {
      const lang = this.$i18next.language || this.$i18n?.locale || 'en'
      return lang.split('-')[0]
    },
    currentSlides() {
      return this.slides[this.tech] || this.slides.ai
    },
    totalSteps() {
      return this.currentSlides.length
    },
    currentSlide() {
      return this.currentSlides[this.step]
    },
    isIntroStep() {
      return this.step === 0
    },
    isLastStep() {
      return this.step === this.totalSteps - 1
    },
    indicatorStep: {
      get() {
        return this.step - 1
      },
      set(newIndicatorIndex) {
        this.step = newIndicatorIndex + 1
      },
    },
  },
  methods: {
    handleClose() {
      this.$root.$emit('closeDubbingModal')
    },
    handleMainBack() {
      if (this.isIntroStep) {
        this.$root.$emit('selectDubbingTechScreen')
      } else {
        this.handleStepBack()
      }
    },
    handleContinue() {
      this.$root.$emit('selectDubbingScreen')
    },
    handleStepForward() {
      if (this.isLastStep) {
        this.handleContinue()
      } else {
        this.step += 1
      }
    },
    handleStepBack() {
      if (this.step > 0) {
        this.step -= 1
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.header {
  padding: 24px;
}

.card-actions {
  width: 100%;
  padding-bottom: 40px;
  gap: 16px;
}

.title {
  line-height: 120%;
}

.subtitle:not(.subtitle-step-0) {
  line-height: 150%;
}

.indicator-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: varFigma(neutral-90);
  margin: 0 4px;
  cursor: pointer;
  transition: background-color 0.3s;

  &.active {
    background-color: varFigma(primary);
  }
}

::v-deep .image-step {
  max-width: 331px;
  max-height: 220px;
  width: 100%;
  height: 100%;
  margin-left: auto;
  margin-right: auto;
  display: block;
}

::v-deep .lazy-image {
  width: 100%;
  height: 100%;
  border-radius: 8px;
  object-fit: cover !important;
  border: 1px solid varFigma(neutral-95-lightest);
}

::v-deep .bold {
  font-weight: 500 !important;
}

a[type='secondary'] {
  color: varFigma(neutral-40-light);
  font-weight: 500;
  &:hover {
    text-decoration: underline;
  }
}

a[type='primary'],
a[type='primary']:visited,
a[type='primary']:active {
  color: varFigma(brand-60-base) !important;
  font-weight: 500;
  
  &:hover {
    text-decoration: underline;
  }
}

@media (min-width: 430px) {
  .title,
  .subtitle {
    white-space: pre-line;
  }
}

@media (min-width: 768px) {
  .subtitle-step-0 {
    justify-content: flex-start;
  }

  .title {
    font-size: 24px !important;
  }
  
  .title.not-intro-step {
    font-size: 18px !important;
  }
  
  .subtitle.not-intro-step {
    font-size: 14px !important;
  }

  .v-card__text.not-intro-step {
    padding-top: 0;
    margin-top: -40px;
  }
}

@media (max-width: 768px) {
  .card-actions {
    padding: 16px 20px;
    border-top: 1px solid varFigma(neutral-95-lightest);
  }

  .v-card__text.not-intro-step {
    justify-content: flex-start !important;
  }

  .header {
    padding: 8px 16px;
  }
}
</style>
